import type { PhoneInput } from 'application/ui/fields/phone/types'
import { AuthPanel } from './types'
import { showError, validateForm } from 'application/utils/form-validation'

/**
 * Контроллер авторизации.
 * Управляет панелями авторизации, валидацией форм и отправкой данных.
 */
export class AuthController {
  /** Контейнер контроллера авторизации */
  private container: HTMLElement
  /** Карта панелей авторизации */
  private panels: Map<string, AuthPanel> = new Map()
  /** Текущая активная панель */
  private activePanel: AuthPanel | null = null
  /** Последний режим восстановления пароля */
  private lastMode: 'initial' | 'email' | 'phone' = 'email'

  /**
   * Создает экземпляр AuthController.
   *
   * @param {HTMLElement} container - Контейнер контроллера авторизации
   */
  constructor(container: HTMLElement) {
    this.container = container
  }

  /**
   * Инициализирует контроллер авторизации.
   * Загружает панели и добавляет обработчики событий.
   *
   * @public
   * @return {void}
   */
  public init() {
    this.loadPanels()
    this.container.addEventListener('click', this.onActionClick)
    this.container.addEventListener('submit', this.onSubmit)
  }

  /**
   * Загружает все панели авторизации из DOM.
   * Находит элементы с data-auth-target и создает панели.
   *
   * @private
   * @return {void}
   */
  private loadPanels(): void {
    const panelElements =
      this.container.querySelectorAll<HTMLElement>('[data-auth-target]')

    panelElements.forEach((element) => {
      const name = element.dataset.authTarget || ''
      const action = element.dataset.action || ''

      if (name) {
        const panel: AuthPanel = { element, name, action }
        this.panels.set(name, panel)

        if (element.classList.contains('is-active')) {
          this.activePanel = panel
        }
      }
    })
  }

  /**
   * Обработчик клика по кнопке действия.
   * Находит кнопку с data-action и вызывает соответствующее действие.
   *
   * @private
   * @param {Event} event - Событие клика
   * @return {void}
   */
  private onActionClick = (event: Event): void => {
    const target = event.target as HTMLElement
    const actionBtn = target.closest('button[data-action]') as HTMLElement

    if (!actionBtn) return

    const action = actionBtn.dataset.action

    if (action) {
      this.handleAction(action)
    }
  }

  /**
   * Обрабатывает действие и переключает на соответствующую панель.
   *
   * @private
   * @param {string} action - Название действия
   * @return {void}
   */
  private handleAction(action: string): void {
    const targetPanel = Array.from(this.panels.values()).find(
      (panel) => panel.action === action
    )

    if (targetPanel) {
      this.switchToPanel(targetPanel)
    } else {
      console.warn('No panel found for action:', action)
    }
  }

  /**
   * Переключает на указанную панель.
   * Скрывает текущую активную панель и показывает новую.
   *
   * @private
   * @param {AuthPanel} panel - Панель для отображения
   * @return {void}
   */
  private switchToPanel(panel: AuthPanel): void {
    if (this.activePanel) {
      this.activePanel.element.classList.remove('is-active')
    }

    panel.element.classList.add('is-active')
    this.activePanel = panel
    this.resetResetPasswordForm()
    this.resetPhoneLoginStep()
  }

  /**
   * Обработчик отправки формы.
   * Валидирует форму и отправляет данные на сервер.
   *
   * @private
   * @param {Event} event - Событие отправки формы
   * @return {void}
   */
  private onSubmit = (event: Event): void => {
    const form = event.target as HTMLFormElement

    if (form.dataset.authNativeSubmit === 'true') {
      return
    }

    event.preventDefault()
    // Handle form submission logic here

    if (!validateForm(form)) {
      return
    }

    this.fetchForm(form).then(({ success, message }) => {
      if (success) {
        if (form.dataset.phoneStep === 'request') {
          this.showPhoneLoginStep(form, 'code')
          return
        }

        if (this.activePanel?.name === 'resetPasswordPanel') {
          this.showResetPasswordSuccess(form)
        } else if (this.activePanel?.name === 'signUpPanel') {
          this.handleAction('successRegistration')
          // Аккаунт может быть создан, а письмо с подтверждением — не
          // отправлено (сбой почтового сервера): тогда сервер вернёт
          // success: true, но с предупреждением в message вместо стандартного
          // «письмо отправлено» — его и показываем вместо текста по умолчанию.
          if (message) {
            const messageEl = this.container.querySelector<HTMLElement>(
              '[data-registration-message]'
            )
            if (messageEl) messageEl.textContent = message
          }
        }

        form.reset()
      }
    })
  }

  /**
   * Отправляет данные формы на сервер.
   * Обрабатывает форматирование телефона и отображение состояний загрузки.
   *
   * @private
   * @param {HTMLFormElement} form - Форма для отправки
   * @return {Promise<Object>} Promise с { success, message } — message заполнен, если сервер
   *   его прислал (например, предупреждение о недоставленном письме)
   */
  private getCsrfToken(): string {
    return (
      document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
        ?.content ?? ''
    )
  }

  private async fetchForm(
    form: HTMLFormElement
  ): Promise<{ success: boolean; message?: string }> {
    const submitBtn = form.querySelector<HTMLButtonElement>(
      'button[type="submit"]'
    )!

    try {
      this.lastMode = 'email'
      const formData = new FormData(form)

      const action = form.getAttribute('action')
      submitBtn.classList.add('loading')

      const response = await fetch(action || '', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: formData
      })

      const data = await response.json()

      if (data.dev_code) {
        console.log(`[DEV] SMS code: ${data.dev_code}`)
      }

      if (data.error) {
        throw new Error(data.error)
      }

      if (response.status !== 200 && response.status !== 201) {
        throw new Error('Ошибка при отправке формы')
      }

      if (data.redirect_to) {
        window.location.href = data.redirect_to
        return { success: true }
      }

      return { success: true, message: data.message }
    } catch (error) {
      showError(form, (error as Error).message)
      return { success: false }
    } finally {
      submitBtn.classList.remove('loading')
    }
  }

  /**
   * Отображает сообщение об успешном восстановлении пароля.
   * Скрывает форму и показывает соответствующее сообщение.
   *
   * @private
   * @param {HTMLFormElement} form - Форма восстановления пароля
   * @return {void}
   */
  private showResetPasswordSuccess = (form: HTMLFormElement) => {
    const formWrapper =
      this.container.querySelector<HTMLElement>('[data-reset-form]')
    formWrapper!.style.display = 'none'

    this.changeResetPasswordMode(form, this.lastMode)
  }

  /**
   * Сбрасывает форму восстановления пароля в начальное состояние.
   * Показывает форму и устанавливает режим в 'initial'.
   *
   * @private
   * @return {void}
   */
  private resetResetPasswordForm = () => {
    const formWrapper =
      this.container.querySelector<HTMLElement>('[data-reset-form]')
    formWrapper!.style.display = 'block'

    this.changeResetPasswordMode(null, 'initial')
  }

  /**
   * Изменяет режим отображения сообщения восстановления пароля.
   * Показывает соответствующее сообщение в зависимости от режима (email или phone).
   *
   * @private
   * @param {HTMLFormElement | null} form - Форма восстановления пароля или null
   * @param {'initial' | 'email' | 'phone'} mode - Режим отображения
   * @return {void}
   */
  private changeResetPasswordMode = (
    form: HTMLFormElement | null,
    mode: 'initial' | 'email' | 'phone'
  ) => {
    const resetMsgElements =
      this.container.querySelectorAll<HTMLElement>('[data-reset-msg]')

    resetMsgElements.forEach((el) => {
      const elMode = el.dataset.resetMsg

      if (elMode === mode) {
        el.style.display = 'block'
      } else {
        el.style.display = 'none'
      }
    })

    if (!form) return

    const sentValueEl = this.container.querySelector<HTMLElement>(
      `[data-reset-msg="${mode}"] [data-sent-value]`
    )

    switch (mode) {
      case 'email': {
        const emailInput = form.querySelector<HTMLInputElement>(
          'input[name="email"]'
        )
        if (sentValueEl && emailInput) {
          sentValueEl.textContent = emailInput.value.trim()
        }
        break
      }
      case 'phone': {
        const phoneInput = form.querySelector<PhoneInput>('[data-phone-input]')
        if (sentValueEl && phoneInput) {
          sentValueEl.textContent = phoneInput.value
        }
        break
      }
    }
  }

  /**
   * Показывает нужный шаг входа по телефону (ввод номера / ввод кода).
   *
   * @private
   * @param {HTMLFormElement | null} form - Форма шага "request", откуда берётся номер телефона
   * @param {'request' | 'code'} step - Шаг для отображения
   * @return {void}
   */
  private showPhoneLoginStep = (
    form: HTMLFormElement | null,
    step: 'request' | 'code'
  ) => {
    const stepElements =
      this.container.querySelectorAll<HTMLElement>('[data-login-msg]')

    stepElements.forEach((el) => {
      el.style.display = el.dataset.loginMsg === step ? 'block' : 'none'
    })

    if (step !== 'code' || !form) return

    const phoneInput = form.querySelector<PhoneInput>('[data-phone-input]')
    const sentValueEl = this.container.querySelector<HTMLElement>(
      '[data-login-msg="code"] [data-sent-value]'
    )
    if (sentValueEl && phoneInput) {
      sentValueEl.textContent = phoneInput.value
    }

    const codeInput = this.container.querySelector<HTMLInputElement>(
      '[data-login-msg="code"] [data-code-input]'
    )
    codeInput?.focus()
  }

  /**
   * Сбрасывает форму входа по телефону в начальное состояние (ввод номера).
   *
   * @private
   * @return {void}
   */
  private resetPhoneLoginStep = () => {
    this.showPhoneLoginStep(null, 'request')
  }
}
